import React from 'react';
import validationConfig from '../../validation-config.json';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Router } from '@uhg-abyss/web/ui/Router';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { V1Table } from '@uhg-abyss/web/ui/Table';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { SharedDialog } from '../../common/SharedDialog';
import { PageSpinner } from '../../common/PageSpinner';
import { formatDisplayDateTime } from '../../common/dateTime/formatDisplayDateTime';
import { navigate } from '../../common/navigate';
import { useGlobalError } from '../../common/ErrorHandler';
import { useAuth, AuthSession } from '../../auth';
import {
  RuntimeSurveyQuestion,
  SurveyEntrySummary,
  SurveyResponseRecord,
  getSurveyEntry,
  getSurveyQuestionsBySurveyId,
  getSurveyResponseByCustomerIdentity,
  saveSurveyResponse,
} from './surveyApi';

const SurveyLayout = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
});

const Page = styled('section', {
  flex: '1 1 0',
  minWidth: 0,
  display: 'grid',
  gap: '1rem',
  boxSizing: 'border-box',
});

const ContextPanel = styled(Card, {
  width: '100%',
  borderRadius: '12px',
  border: '1px solid #c8d8e6',
  backgroundColor: '#f5f9fc',
  padding: '1rem 1.25rem',
  boxSizing: 'border-box',
  display: 'grid',
  gap: '0.35rem',
});

const ContextLabel = styled(Text, {
  margin: 0,
  color: '#345570',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
});

const ContextPath = styled(Text, {
  margin: 0,
  color: '$web.semantic.color.text.content.primary',
  fontSize: '0.95rem',
  fontWeight: 600,
});

const ContextBody = styled(Text, {
  margin: 0,
  color: '$web.semantic.color.text.content.primary',
});

const DetailTable = styled('table', {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
});

const DetailThead = styled('thead', {
  backgroundColor: '#dceaf3',
});

const DetailTh = styled('th', {
  padding: '0.35rem 0.75rem',
  fontWeight: 700,
  color: '#345570',
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
  textAlign: 'left',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  borderBottom: '1px solid #b8cad8',
});

const ThLabel = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
  flexWrap: 'wrap',
});

const DetailTd = styled('td', {
  padding: '0.35rem 0.75rem',
  fontWeight: 500,
  color: '$web.semantic.color.text.content.primary',
  fontSize: '0.88rem',
  wordBreak: 'break-word',
});

const SummaryPanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #c8d8e6',
  padding: '1.25rem',
  boxSizing: 'border-box',
});

const ActionRow = styled('div', {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap',
});

const VerticalRail = styled('div', {
  position: 'sticky',
  top: 0,
  height: 'calc(100vh - 72px - 36px)',
  width: '28px',
  flexShrink: 0,
  alignSelf: 'flex-start',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '2rem',
  paddingBottom: '2rem',
  boxSizing: 'border-box',
  backgroundColor: 'rgba(245, 249, 252, 0.95)',
  borderRight: '1px solid #dbe5ee',
});

const RailTrack = styled('div', {
  position: 'relative',
  width: '4px',
  flex: '1 1 0',
  backgroundColor: '#dbe5ee',
  borderRadius: '2px',
  overflow: 'visible',
});

const RailFill = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: '#345570',
  borderRadius: '2px',
  transition: 'height 0.35s ease',
  pointerEvents: 'none',
});

const RailDot = styled('button', {
  position: 'absolute',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  border: '2px solid #345570',
  cursor: 'pointer',
  padding: 0,
  transition: 'background-color 0.25s ease',
  flexShrink: 0,
});

const RailLabel = styled('span', {
  writingMode: 'vertical-lr',
  fontSize: '0.6rem',
  color: '#345570',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  transform: 'rotate(180deg)',
  whiteSpace: 'nowrap',
  userSelect: 'none',
});

const QuestionList = styled('div', {
  display: 'grid',
  gap: '1rem',
});

const QuestionCard = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #dbe5ee',
  borderLeft: '4px solid #7ea1bd',
  padding: '1rem 1.1rem',
  background: 'linear-gradient(180deg, #f8fbfe 0%, #ffffff 42%)',
  boxSizing: 'border-box',
  display: 'grid',
  gap: '0.65rem',
});

const QuestionMeta = styled('div', {
  display: 'grid',
  gap: '0.6rem',
});

const QuestionPrompt = styled('div', {
  display: 'grid',
  gap: '0.4rem',
  padding: '0.5rem 0',
});

const TitleRow = styled('div', {
  display: 'flex',
  gap: '0.4rem',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
});

const HelpLink = styled('a', {
  fontSize: '0.875rem',
  color: '$web.semantic.color.text.link.primary',
  textDecoration: 'underline',
  '&:hover': { textDecoration: 'none' },
});

const TitleHelpLink = styled(HelpLink, {
  fontSize: '1.0rem',
  fontWeight: 800,
  whiteSpace: 'nowrap',
  color: 'crimson',
});

const HiddenLabelChoiceList = styled('div', {
  '& [class*="radio-group-label"]': {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
});

const Field = styled('div', {
  display: 'grid',
  gap: '0.45rem',
});

const Label = styled('label', {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

const Input = styled('input', {
  width: '100%',
  border: '1px solid #a0b5c8',
  borderRadius: '10px',
  padding: '0.7rem 0.8rem',
  boxSizing: 'border-box',
  backgroundColor: '#f9fbfd',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  '&:focus': {
    outline: '2px solid #7ea1bd',
    outlineOffset: '1px',
    borderColor: '#7ea1bd',
    backgroundColor: '#ffffff',
  },
});

const Textarea = styled('textarea', {
  width: '100%',
  minHeight: '112px',
  border: '1px solid #a0b5c8',
  borderRadius: '10px',
  padding: '0.7rem 0.8rem',
  boxSizing: 'border-box',
  resize: 'vertical',
  backgroundColor: '#f9fbfd',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  '&:focus': {
    outline: '2px solid #7ea1bd',
    outlineOffset: '1px',
    borderColor: '#7ea1bd',
    backgroundColor: '#ffffff',
  },
});

const ChoiceList = styled('div', {
  display: 'grid',
  gap: '0.55rem',
});

const ErrorSummary = styled(Card, {
  width: '100%',
  borderRadius: '14px',
  border: '1px solid #e2b3b3',
  backgroundColor: '#fff7f7',
  padding: '0.95rem 1rem',
  boxSizing: 'border-box',
  display: 'grid',
  gap: '0.45rem',
});

const ErrorLinkList = styled('ul', {
  margin: 0,
  paddingLeft: '1.1rem',
  display: 'grid',
  gap: '0.35rem',
});

const PreviewList = styled('div', {
  display: 'grid',
  gap: '1.25rem',
});

const AttestationBox = styled('div', {
  borderRadius: '10px',
  border: '1px solid #bfdbfe',
  backgroundColor: '#eff6ff',
  padding: '1rem 1.1rem',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.25rem',
  '& label::after': {
    content: '"*"',
    color: 'darkred',
    fontSize: '1.125rem',
    fontWeight: 700,
    lineHeight: 1.5,
    marginRight: '0.25rem',
  },
});

const RequiredAsterisk = styled('span', {
  color: 'darkred',
  fontWeight: 700,
  fontSize: '1.125rem',
  lineHeight: 1.5,
});

const SuccessLinkReference = styled('span', {
  color: '#005eb8',
  fontWeight: 700,
});

type SurveyAnswerState = {
  value: string;
  selectedOptionIds: number[];
};

type SurveyAnswerMap = Record<number, SurveyAnswerState>;

const HOME_INTERNAL_SEARCH_KEY = 'rfi.internalSearchContext';
const TYPE_TEXT = 1;
const TYPE_MULTILINE = 2;
const TYPE_CHECKBOX = 3;
const TYPE_RADIO = 4;
const TYPE_NUMERIC = 5;
const TYPE_ALPHANUMERIC = 6;
const TYPE_DATE = 7;
// Currency has no fixed DB question_type_id (legacy never hardcodes one either),
// so this sentinel never matches by id and Currency is resolved purely by label alias.
const TYPE_CURRENCY = -1;

function parsePolicyValues(
  policyList: string | null | undefined,
  policyNumber: string | null | undefined
) {
  const values = (policyList ?? '')
    .split(',')
    .map((value) => {
      return value.trim();
    })
    .filter((value) => {
      return value.length > 0;
    });

  if (values.length > 0) {
    return values;
  }

  const fallback = (policyNumber ?? '').trim();
  return fallback ? [fallback] : [];
}

function getEntryIdFromPath() {
  const segments = window.location.pathname.split('/').filter(Boolean);
  return decodeURIComponent(segments[2] ?? '');
}

function isHistoryReadOnlyMode() {
  const params = new URLSearchParams(window.location.search);
  return params.get('fromHistory') === 'true';
}

function navigateTo(path: string) {
  navigate(path);
}

function readInternalSearchContext() {
  const raw = window.sessionStorage.getItem(HOME_INTERNAL_SEARCH_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as {
      searchKey?: string | null;
      quickFilter?: string;
      selectedCustomerNumber?: string;
      selectedPolicyNumber?: string;
      fromSurvey?: boolean;
      returnAction?: 'cancel' | 'save';
    };
  } catch {
    return null;
  }
}

function writeInternalSearchContext(args: {
  entry: SurveyEntrySummary;
  returnAction: 'cancel' | 'save';
}) {
  const previous = readInternalSearchContext();

  window.sessionStorage.setItem(
    HOME_INTERNAL_SEARCH_KEY,
    JSON.stringify({
      searchKey: previous?.searchKey ?? '',
      quickFilter: previous?.quickFilter ?? '',
      selectedCustomerNumber: args.entry.selectedCustomer.customerNumber,
      selectedPolicyNumber: args.entry.selectedCustomer.policyNumber,
      fromSurvey: true,
      returnAction: args.returnAction,
    })
  );
}

function normalizeQuestionTypeName(value: string | null | undefined) {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function matchesQuestionType(
  question: RuntimeSurveyQuestion,
  expectedId: number,
  aliases: string[]
) {
  if (question.questionTypeId === expectedId) {
    return true;
  }

  const normalizedTypeName = normalizeQuestionTypeName(question.questionType);
  return (
    normalizedTypeName.length > 0 &&
    aliases.some((alias) => {
      return normalizeQuestionTypeName(alias) === normalizedTypeName;
    })
  );
}

function isCheckboxQuestion(question: RuntimeSurveyQuestion) {
  return matchesQuestionType(question, TYPE_CHECKBOX, ['checkbox']);
}

function isRadioQuestion(question: RuntimeSurveyQuestion) {
  return matchesQuestionType(question, TYPE_RADIO, ['radio']);
}

function isSingleLineTextQuestion(question: RuntimeSurveyQuestion) {
  return (
    matchesQuestionType(question, TYPE_TEXT, ['textbox', 'text']) ||
    matchesQuestionType(question, TYPE_ALPHANUMERIC, [
      'alphanumeric',
      'alphanumric',
      'alphanumerictext',
    ]) ||
    matchesQuestionType(question, 9, [
      'alphanumeric',
      'alphanumric',
      'alphanumerictext',
    ])
  );
}

function isMultilineQuestion(question: RuntimeSurveyQuestion) {
  return matchesQuestionType(question, TYPE_MULTILINE, [
    'multiline',
    'textarea',
  ]);
}

function isNumericQuestion(question: RuntimeSurveyQuestion) {
  return matchesQuestionType(question, TYPE_NUMERIC, ['numeric', 'number']);
}

function isDateQuestion(question: RuntimeSurveyQuestion) {
  return (
    matchesQuestionType(question, TYPE_DATE, ['date']) ||
    matchesQuestionType(question, 11, ['date'])
  );
}

function isCurrencyQuestion(question: RuntimeSurveyQuestion) {
  return matchesQuestionType(question, TYPE_CURRENCY, ['currency']);
}

function stripCurrencyFormatting(value: string) {
  return value.replace(/[$,\s]/g, '');
}

function getCurrencyNumericValue(value: string | null | undefined) {
  const normalized = stripCurrencyFormatting((value ?? '').toString());
  if (!normalized) {
    return 0;
  }

  const numericValue = Number(normalized);
  return Number.isNaN(numericValue) ? 0 : numericValue;
}

function sanitizeCurrencyInput(value: string) {
  return value.replace(/[^0-9.,]/g, '');
}

function formatCurrencyDisplay(value: string | null | undefined) {
  const raw = (value ?? '').toString();
  const normalized = stripCurrencyFormatting(raw);
  if (!normalized) {
    return '';
  }

  const numericValue = Number(normalized);
  if (Number.isNaN(numericValue)) {
    return raw;
  }

  const decimalParts = normalized.split('.');
  if (decimalParts.length > 1) {
    const decimalPlaces = decimalParts[1]!.length;
    return `$${numericValue.toLocaleString('en-US', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    })}`;
  }

  return `$${numericValue.toLocaleString('en-US', {
    maximumFractionDigits: 0,
  })}`;
}

function isLengthValidatedQuestion(question: RuntimeSurveyQuestion) {
  return (
    isSingleLineTextQuestion(question) ||
    isMultilineQuestion(question) ||
    isNumericQuestion(question)
  );
}

function isOptionQuestion(question: RuntimeSurveyQuestion) {
  return isCheckboxQuestion(question) || isRadioQuestion(question);
}

function normalizeOptionText(value: string | null | undefined) {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function inferSelectedOptionIds(
  question: RuntimeSurveyQuestion,
  responseText: string | null | undefined
) {
  const normalizedResponse = normalizeOptionText(responseText);
  if (!normalizedResponse) {
    return [] as number[];
  }

  if (isRadioQuestion(question)) {
    const matchedOption = question.options.find((option) => {
      return normalizeOptionText(option.optionTitle) === normalizedResponse;
    });
    return matchedOption ? [matchedOption.questionOptionId] : [];
  }

  if (isCheckboxQuestion(question)) {
    const selectedIds: number[] = [];
    const normalizedParts = normalizedResponse
      .split(',')
      .map((part) => {
        return normalizeOptionText(part);
      })
      .filter(Boolean);

    for (const option of question.options) {
      const normalizedTitle = normalizeOptionText(option.optionTitle);
      if (normalizedParts.includes(normalizedTitle)) {
        selectedIds.push(option.questionOptionId);
      }
    }

    return selectedIds;
  }

  return [] as number[];
}

function hasAnswer(
  question: RuntimeSurveyQuestion,
  answer: SurveyAnswerState | undefined
) {
  if (!answer) {
    return false;
  }

  if (isOptionQuestion(question)) {
    return (
      answer.selectedOptionIds.length > 0 || answer.value.trim().length > 0
    );
  }

  return answer.value.trim().length > 0;
}

type ValidationConfigRule = {
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  min?: number;
  max?: number;
  errorMessage?: string;
};

const SURVEY_VALIDATION = {
  requiredMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['requiredMessage'] ?? 'This question is required.',
  numericMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['numericMessage'] ?? 'Enter a valid number.',
  minLengthMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['minLengthMessage'] ?? 'Enter at least {minLength} characters.',
  maxLengthMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['maxLengthMessage'] ?? 'Enter no more than {maxLength} characters.',
  invalidFormatMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['invalidFormatMessage'] ??
    'Enter a value that matches the required format.',
  attestationRequiredMessage:
    (validationConfig as Record<string, unknown>)['SURVEY_PAGE']?.[
      '_defaults'
    ]?.['attestationRequiredMessage'] ??
    'Attestation is required before final submission.',
};

function getConfigRule(
  dbCode: string | null | undefined
): ValidationConfigRule | null {
  if (!dbCode) {
    return null;
  }

  const surveyPage = (validationConfig as Record<string, unknown>)[
    'SURVEY_PAGE'
  ] as Record<string, ValidationConfigRule> | undefined;
  return surveyPage?.[dbCode] ?? null;
}

function applyValidationConfig(
  question: RuntimeSurveyQuestion
): RuntimeSurveyQuestion {
  const rule = getConfigRule(question.surveyQuestionDbCode);
  if (!rule) {
    return question;
  }

  return {
    ...question,
    minLength: rule.minLength ?? question.minLength,
    maxLength: rule.maxLength ?? question.maxLength,
    required: rule.required ?? question.required,
    errorMessage: rule.errorMessage ?? question.errorMessage,
  };
}

function getRequiredValidationMessage(
  q: RuntimeSurveyQuestion,
  answer: SurveyAnswerState | undefined
): string | null {
  if (q.required && !hasAnswer(q, answer)) {
    return SURVEY_VALIDATION.requiredMessage;
  }

  return null;
}

function getNumericValidationMessage(
  q: RuntimeSurveyQuestion,
  value: string
): string | null {
  if (isNumericQuestion(q) && Number.isNaN(Number(value))) {
    return q.errorMessage ?? SURVEY_VALIDATION.numericMessage;
  }

  return null;
}

function getCurrencyValidationMessage(
  q: RuntimeSurveyQuestion,
  value: string
): string | null {
  if (!isCurrencyQuestion(q)) {
    return null;
  }

  const numericValue = Number(stripCurrencyFormatting(value));
  if (Number.isNaN(numericValue)) {
    return q.errorMessage ?? SURVEY_VALIDATION.numericMessage;
  }

  if (
    (q.minLength != null && numericValue < q.minLength) ||
    (q.maxLength != null && numericValue > q.maxLength)
  ) {
    return getCurrencyLimitValidationMessage(q);
  }

  return null;
}

function getCurrencyLimitValidationMessage(q: RuntimeSurveyQuestion) {
  return q.errorMessage ?? SURVEY_VALIDATION.invalidFormatMessage;
}

function getInputFormatValidationMessage(
  q: RuntimeSurveyQuestion,
  value: string
): string | null {
  if (!q.inputFormat || q.inputFormat === 'None') {
    return null;
  }

  const pattern = new RegExp(q.inputFormat);
  if (pattern.test(value)) {
    return null;
  }

  return q.errorMessage ?? SURVEY_VALIDATION.invalidFormatMessage;
}

function getMinLengthValidationMessage(
  q: RuntimeSurveyQuestion,
  value: string
): string | null {
  if (
    isLengthValidatedQuestion(q) &&
    q.minLength != null &&
    value.length < q.minLength
  ) {
    return (
      q.errorMessage ??
      SURVEY_VALIDATION.minLengthMessage.replace(
        '{minLength}',
        String(q.minLength)
      )
    );
  }

  return null;
}

function getMaxLengthValidationMessage(
  q: RuntimeSurveyQuestion,
  value: string
): string | null {
  if (
    isLengthValidatedQuestion(q) &&
    q.maxLength != null &&
    value.length > q.maxLength
  ) {
    return SURVEY_VALIDATION.maxLengthMessage.replace(
      '{maxLength}',
      String(q.maxLength)
    );
  }

  return null;
}

function getValidationMessage(
  question: RuntimeSurveyQuestion,
  answer: SurveyAnswerState | undefined,
  prefillValue?: string
) {
  const q = applyValidationConfig(question);

  const requiredMessage = getRequiredValidationMessage(q, answer);
  if (requiredMessage !== null) {
    return requiredMessage;
  }

  if (!answer || !answer.value.trim()) {
    return null;
  }

  const value = answer.value.trim();

  if (prefillValue && value === prefillValue.trim()) {
    return null;
  }

  return (
    getNumericValidationMessage(q, value) ??
    getCurrencyValidationMessage(q, value) ??
    getInputFormatValidationMessage(q, value) ??
    getMinLengthValidationMessage(q, value) ??
    getMaxLengthValidationMessage(q, value)
  );
}

function getQuestionAnchorId(questionId: number) {
  return `survey-question-${questionId}`;
}

function getQuestionInputId(
  question: RuntimeSurveyQuestion,
  visibleIndex: number
) {
  return `SURVEY_PAGE.${question.questionTypeId}_${visibleIndex + 1}`;
}

function getPreferredFirstQuestionAnswerValue(selectedCustomer: {
  employerGroupName: string;
  customerNumber: string;
}) {
  const employerGroupName = selectedCustomer.employerGroupName.trim();
  if (employerGroupName.length > 0) {
    return employerGroupName;
  }

  return selectedCustomer.customerNumber.trim();
}

function buildHydratedAnswers(
  questions: RuntimeSurveyQuestion[],
  savedResponse: SurveyResponseRecord | null,
  selectedCustomer: {
    employerGroupName: string;
    customerNumber: string;
  }
) {
  const nextAnswers: SurveyAnswerMap = {};
  const questionById = new Map<number, RuntimeSurveyQuestion>();

  for (const question of questions) {
    questionById.set(question.questionId, question);
  }

  for (const detail of savedResponse?.surveyResponseDetailsDTOList ?? []) {
    const question = questionById.get(detail.questionId);
    const selectedOptionIds =
      detail.surveyResponseDetailsOptionsDTOList.length > 0
        ? detail.surveyResponseDetailsOptionsDTOList.map((option) => {
            return option.questionOptionId;
          })
        : question
          ? inferSelectedOptionIds(question, detail.response)
          : [];

    nextAnswers[detail.questionId] = {
      value:
        question && isCurrencyQuestion(question)
          ? formatCurrencyDisplay(detail.response)
          : (detail.response ?? ''),
      selectedOptionIds,
    };
  }

  const firstQuestion = questions[0];
  const preferredFirstAnswerValue =
    getPreferredFirstQuestionAnswerValue(selectedCustomer);
  if (
    firstQuestion &&
    !isOptionQuestion(firstQuestion) &&
    preferredFirstAnswerValue.length > 0
  ) {
    const existingFirstAnswer = nextAnswers[firstQuestion.questionId];
    const shouldReplaceWithPrefill = !hasAnswer(
      firstQuestion,
      existingFirstAnswer
    );

    if (shouldReplaceWithPrefill) {
      nextAnswers[firstQuestion.questionId] = {
        value: preferredFirstAnswerValue,
        selectedOptionIds: [],
      };
    }
  }

  return nextAnswers;
}

function computeVisibleQuestionIds(
  questions: RuntimeSurveyQuestion[],
  answers: SurveyAnswerMap
) {
  if (questions.length === 0) {
    return [] as number[];
  }

  const indexByQuestionId = new Map<number, number>();
  for (let index = 0; index < questions.length; index += 1) {
    indexByQuestionId.set(questions[index]!.questionId, index);
  }

  const visibleQuestionIds: number[] = [];
  const visited = new Set<number>();
  let currentIndex = 0;

  while (
    currentIndex >= 0 &&
    currentIndex < questions.length &&
    !visited.has(currentIndex)
  ) {
    visited.add(currentIndex);
    const question = questions[currentIndex]!;
    visibleQuestionIds.push(question.questionId);

    const answer = answers[question.questionId];
    if (!hasAnswer(question, answer)) {
      const hasLaterAnswer = questions.slice(currentIndex + 1).some((nextQuestion) => {
        return hasAnswer(nextQuestion, answers[nextQuestion.questionId]);
      });
      if (!isRadioQuestion(question) && hasLaterAnswer) {
        currentIndex += 1;
        continue;
      }
      break;
    }

    if (isRadioQuestion(question)) {
      const selectedOptionId = answer.selectedOptionIds[0];
      const matchingRule = question.branchRules.find((rule) => {
        return rule.questionOptionId === selectedOptionId;
      });
      if (matchingRule) {
        const jumpIndex = indexByQuestionId.get(matchingRule.jumpToQuestionId);
        currentIndex = jumpIndex ?? currentIndex + 1;
        continue;
      }
    }

    currentIndex += 1;
  }

  return visibleQuestionIds;
}

function computeProgressState(
  questions: RuntimeSurveyQuestion[],
  answers: SurveyAnswerMap
) {
  if (questions.length === 0) {
    return {
      totalQuestionCount: 0,
      completedCount: 0,
      progressPct: 0,
      completedQuestionIds: new Set<number>(),
    };
  }

  const indexByQuestionId = new Map<number, number>();
  for (let index = 0; index < questions.length; index += 1) {
    indexByQuestionId.set(questions[index]!.questionId, index);
  }

  const skippedQuestionIds = new Set<number>();
  const completedQuestionIds = new Set<number>();
  const visited = new Set<number>();
  let currentIndex = 0;

  while (
    currentIndex >= 0 &&
    currentIndex < questions.length &&
    !visited.has(currentIndex)
  ) {
    visited.add(currentIndex);
    const question = questions[currentIndex]!;
    const answer = answers[question.questionId];
    const isAnswered = hasAnswer(question, answer);

    if (isAnswered) {
      completedQuestionIds.add(question.questionId);
    }

    if (isRadioQuestion(question) && isAnswered) {
      const selectedOptionId = answer.selectedOptionIds[0];
      const matchingRule = question.branchRules.find((rule) => {
        return rule.questionOptionId === selectedOptionId;
      });
      if (matchingRule) {
        const jumpIndex =
          indexByQuestionId.get(matchingRule.jumpToQuestionId) ??
          currentIndex + 1;

        if (jumpIndex > currentIndex + 1) {
          for (
            let skippedIndex = currentIndex + 1;
            skippedIndex < jumpIndex;
            skippedIndex += 1
          ) {
            const skippedQuestionId = questions[skippedIndex]!.questionId;
            skippedQuestionIds.add(skippedQuestionId);
            completedQuestionIds.add(skippedQuestionId);
          }
        }

        currentIndex = jumpIndex;
        continue;
      }
    }

    if (!isAnswered) {
      break;
    }

    currentIndex += 1;
  }

  const totalQuestionCount = questions.length;
  const completedCount = Math.min(
    totalQuestionCount,
    completedQuestionIds.size
  );
  const progressPct =
    totalQuestionCount > 0
      ? Math.round((completedCount / totalQuestionCount) * 100)
      : 0;

  return {
    totalQuestionCount,
    completedCount,
    progressPct,
    completedQuestionIds,
  };
}

function pruneAnswersToVisible(
  answers: SurveyAnswerMap,
  visibleQuestionIds: number[]
) {
  const visibleSet = new Set(visibleQuestionIds);
  const nextAnswers: SurveyAnswerMap = {};

  for (const [questionId, answer] of Object.entries(answers)) {
    const numericQuestionId = Number(questionId);
    if (visibleSet.has(numericQuestionId)) {
      nextAnswers[numericQuestionId] = answer;
    }
  }

  return nextAnswers;
}

function resolveAnswersForValidation(args: {
  questions: RuntimeSurveyQuestion[];
  answers: SurveyAnswerMap;
}) {
  let nextAnswers: SurveyAnswerMap = { ...args.answers };

  for (const question of args.questions) {
    if (!isOptionQuestion(question)) {
      continue;
    }

    const currentAnswer = nextAnswers[question.questionId];
    if (!currentAnswer) {
      continue;
    }

    if (
      currentAnswer.selectedOptionIds.length === 0 &&
      currentAnswer.value.trim().length > 0
    ) {
      const inferredOptionIds = inferSelectedOptionIds(
        question,
        currentAnswer.value
      );
      if (inferredOptionIds.length > 0) {
        nextAnswers[question.questionId] = {
          ...currentAnswer,
          selectedOptionIds: inferredOptionIds,
        };
      }
    }
  }

  const nextVisibleQuestionIds = computeVisibleQuestionIds(
    args.questions,
    nextAnswers
  );
  const prunedAnswers = pruneAnswersToVisible(
    nextAnswers,
    nextVisibleQuestionIds
  );

  return {
    answers: prunedAnswers,
    visibleQuestionIds: nextVisibleQuestionIds,
    visibleQuestions: args.questions.filter((question) => {
      return nextVisibleQuestionIds.includes(question.questionId);
    }),
  };
}

function getAnswerText(
  question: RuntimeSurveyQuestion,
  answer: SurveyAnswerState | undefined
) {
  if (!answer || !hasAnswer(question, answer)) {
    return 'Not answered';
  }

  if (isOptionQuestion(question)) {
    const selectedLabels = question.options
      .filter((option) => {
        return answer.selectedOptionIds.includes(option.questionOptionId);
      })
      .map((option) => {
        return option.optionTitle;
      });
    return selectedLabels.length > 0
      ? selectedLabels.join(', ')
      : answer.value.trim();
  }

  return answer.value.trim();
}

function getOptionTitle(
  question: RuntimeSurveyQuestion,
  questionOptionId: number
) {
  return (
    question.options.find((option) => {
      return option.questionOptionId === questionOptionId;
    })?.optionTitle ?? ''
  );
}

function buildSavePayload(args: {
  entry: SurveyEntrySummary;
  sessionDisplayName: string;
  persistedUserId: number;
  questions: RuntimeSurveyQuestion[];
  answers: SurveyAnswerMap;
  visibleQuestionIds: number[];
  savedResponse: SurveyResponseRecord | null;
  isFinalSubmit: boolean;
}) {
  const existingDetailsByQuestionId = new Map<
    number,
    SurveyResponseRecord['surveyResponseDetailsDTOList'][number]
  >();
  for (const detail of args.savedResponse?.surveyResponseDetailsDTOList ?? []) {
    existingDetailsByQuestionId.set(detail.questionId, detail);
  }

  const visibleSet = new Set(args.visibleQuestionIds);
  const details = args.questions
    .filter((question) => {
      return (
        visibleSet.has(question.questionId) &&
        hasAnswer(question, args.answers[question.questionId])
      );
    })
    .map((question) => {
      const answer = args.answers[question.questionId]!;
      const existingDetail = existingDetailsByQuestionId.get(
        question.questionId
      );
      const existingOptionsByQuestionOptionId = new Map<
        number,
        string | null
      >();

      for (const option of existingDetail?.surveyResponseDetailsOptionsDTOList ??
        []) {
        existingOptionsByQuestionOptionId.set(
          option.questionOptionId,
          option.rdOptionsId ?? null
        );
      }

      return {
        responseDetailsId: existingDetail?.responseDetailsId ?? null,
        surveyResponseId: args.savedResponse?.surveyResponseId ?? null,
        questionId: question.questionId,
        response: isOptionQuestion(question)
          ? getAnswerText(question, answer)
          : isCurrencyQuestion(question)
            ? getCurrencyNumericValue(answer.value).toString()
            : answer.value.trim(),
        surveyResponseDetailsOptionsDTOList: answer.selectedOptionIds.map(
          (questionOptionId) => {
            return {
              rdOptionsId:
                existingOptionsByQuestionOptionId.get(questionOptionId) ?? null,
              responseDetailsId: existingDetail?.responseDetailsId ?? null,
              questionOptionId,
            };
          }
        ),
      };
    });

  return {
    surveyResponseId: args.savedResponse?.surveyResponseId ?? null,
    surveyId: args.entry.activeSurvey.surveyId,
    userName: args.sessionDisplayName,
    userId: args.persistedUserId,
    status: args.isFinalSubmit ? 'Complete' : 'In Process',
    customerNumber: args.entry.selectedCustomer.customerNumber,
    fundingType: args.entry.selectedCustomer.fundingType,
    reportingYear: Number(args.entry.selectedCustomer.surveyYear),
    attested: args.isFinalSubmit ? 1 : 0,
    startDate: args.savedResponse?.startDate ?? new Date().toISOString(),
    completedDate: args.isFinalSubmit ? new Date().toISOString() : null,
    surveyResponseDetailsDTOList: details,
  } satisfies SurveyResponseRecord;
}

function buildSaveSuccessDescription(isFinalSubmit: boolean) {
  if (!isFinalSubmit) {
    return (
      <React.Fragment>
        Your RFI is <strong>incomplete</strong>. To <strong>EDIT</strong> your
        responses, click the{' '}
        <SuccessLinkReference data-success-link-reference="true">
          Employer Group Name
        </SuccessLinkReference>{' '}
        link on the home screen. This will re-open your RFI for editing.
        <br />
        <br />
        RFI&apos;s with a status of In Process are considered incomplete and
        will <strong>not</strong> be included in the submission to CMS.
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      To <strong>EDIT</strong> your responses, simply click on the{' '}
      <SuccessLinkReference data-success-link-reference="true">
        Employer Group Name
      </SuccessLinkReference>{' '}
      link on the home screen. This will re-open your RFI for editing.
      <br />
      To <strong>DOWNLOAD</strong> a copy of your response, click on the{' '}
      <SuccessLinkReference data-success-link-reference="true">
        Download
      </SuccessLinkReference>{' '}
      link on the home screen.
    </React.Fragment>
  );
}

function getSaveSessionError(
  entry: SurveyEntrySummary | null,
  session: AuthSession | null
): { title: string; message: string; isAuthError: true } | null {
  if (!entry || !session?.token) {
    return {
      title: 'Session expired',
      message: 'No authenticated session found. Please sign in again.',
      isAuthError: true,
    };
  }
  return null;
}

function resolvePersistedUserId(
  session: AuthSession | null,
  savedResponse: SurveyResponseRecord | null
): number | null {
  return session?.persisted?.user_id ?? savedResponse?.userId ?? null;
}

function shouldBlockFinalSubmit(issuesCount: number, attested: boolean) {
  return issuesCount > 0 || !attested;
}

function getSurveyHeadingText(entry: SurveyEntrySummary | null): string {
  if (!entry) {
    return 'Survey workspace';
  }

  return `${entry.selectedCustomer.surveyYear} Survey - ${entry.selectedCustomer.employerGroupName || entry.selectedCustomer.customerNumber}`;
}

function SurveyProgressRail(props: {
  isLoading: boolean;
  entry: SurveyEntrySummary | null;
  questions: RuntimeSurveyQuestion[];
  visibleQuestionIdSet: Set<number>;
  progressState: ReturnType<typeof computeProgressState>;
  onFocusQuestion: (questionId: number) => void;
}) {
  if (
    props.isLoading ||
    props.entry === null ||
    props.questions.length === 0
  ) {
    return null;
  }

  return (
    <VerticalRail
      role="progressbar"
      aria-valuenow={props.progressState.progressPct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Survey progress"
    >
      <RailLabel>
        {props.progressState.completedCount}/
        {props.progressState.totalQuestionCount}
      </RailLabel>
      <RailTrack>
        <RailFill
          style={{ height: `${props.progressState.progressPct}%` }}
        />
        {props.questions.map((question, index) => {
          const isVisible = props.visibleQuestionIdSet.has(
            question.questionId
          );
          const isCompleted = props.progressState.completedQuestionIds.has(
            question.questionId
          );
          const topPct =
            props.questions.length > 1
              ? (index / (props.questions.length - 1)) * 100
              : 50;
          return (
            <RailDot
              key={question.questionId}
              type="button"
              aria-label={`Question ${index + 1}${isCompleted ? ', completed' : ''}${isVisible ? '' : ', not currently visible'}`}
              disabled={!isVisible}
              style={{
                top: `${topPct}%`,
                backgroundColor: isCompleted ? '#345570' : '#ffffff',
              }}
              onClick={() => {
                props.onFocusQuestion(question.questionId);
              }}
            />
          );
        })}
      </RailTrack>
    </VerticalRail>
  );
}

function SurveyContextPanel(props: {
  entry: SurveyEntrySummary | null;
  isInternalUser: boolean;
  hasPolicyOverflowRow: boolean;
  policyDisplayValue: string;
  detailColumnCount: number;
}) {
  return (
    <ContextPanel>
      <ContextLabel>Employer Group Information</ContextLabel>
      <ContextPath>Home / Survey workspace</ContextPath>
      {props.entry ? (
        <DetailTable>
          <DetailThead>
            <tr>
              <DetailTh>Employer Group Name</DetailTh>
              <DetailTh>Employer Group Employer Identification Number (EIN)</DetailTh>
              <DetailTh>
                <ThLabel>
                  Funding Type
                  <Tooltip
                    content={
                      <React.Fragment>
                        <div>
                          <strong>ASO:</strong> Administrative Services
                          Only
                        </div>
                        <div>
                          <strong>FI:</strong> Fully Insured
                        </div>
                        <div>
                          <strong>LF:</strong> Level Funded
                        </div>
                        <div>
                          <strong>MP:</strong> Minimum Premium
                        </div>
                        <div>
                          <strong>MMP:</strong> Modified Minimum Premium
                        </div>
                      </React.Fragment>
                    }
                    position="top"
                    variant="white"
                    delayDuration={200}
                  >
                    <span
                      aria-label="Funding type information"
                      style={{
                        cursor: 'help',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '1.1rem',
                        height: '1.1rem',
                        borderRadius: '50%',
                        backgroundColor: '#345570',
                        color: '#ffffff',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        lineHeight: 1,
                        textTransform: 'none',
                        letterSpacing: 0,
                        flexShrink: 0,
                      }}
                    >
                      ?
                    </span>
                  </Tooltip>
                </ThLabel>
              </DetailTh>
              <DetailTh>Reference Year</DetailTh>
              {!props.hasPolicyOverflowRow ? (
                <DetailTh>Policy List</DetailTh>
              ) : null}
            </tr>
          </DetailThead>
          <tbody>
            <tr>
              <DetailTd>
                {props.entry.selectedCustomer.employerGroupName || '—'}
              </DetailTd>
              <DetailTd>
                {props.entry.selectedCustomer.employerGroupEIN || '—'}
              </DetailTd>
              <DetailTd>
                {props.entry.selectedCustomer.fundingType || '—'}
              </DetailTd>
              <DetailTd>
                {props.entry.selectedCustomer.surveyYear || '—'}
              </DetailTd>
              {!props.hasPolicyOverflowRow ? (
                <DetailTd>{props.policyDisplayValue}</DetailTd>
              ) : null}
            </tr>
            {props.hasPolicyOverflowRow ? (
              <tr>
                <DetailTd colSpan={props.detailColumnCount}>
                  <strong>Policy Numbers:</strong> {props.policyDisplayValue}
                </DetailTd>
              </tr>
            ) : null}
          </tbody>
        </DetailTable>
      ) : (
        <ContextBody>
          Loading selected employer group context...
        </ContextBody>
      )}
    </ContextPanel>
  );
}

function SurveyValidationSummary(props: {
  validationSummary: Array<{
    questionId: number;
    questionTitle: string;
    message: string;
  }>;
}) {
  if (props.validationSummary.length === 0) {
    return null;
  }

  return (
    <ErrorSummary>
      <Text color="$web.semantic.color.text.status.error">
        Please resolve the following issues before continuing.
      </Text>
      <ErrorLinkList>
        {props.validationSummary.map((issue) => {
          return (
            <li key={issue.questionId}>
              <a href={`#${getQuestionAnchorId(issue.questionId)}`}>
                {issue.questionTitle}
              </a>
              : {issue.message}
            </li>
          );
        })}
      </ErrorLinkList>
    </ErrorSummary>
  );
}

function SurveyLoadingOrEmptyPanel(props: {
  isLoading: boolean;
  entry: SurveyEntrySummary | null;
  visibleQuestionsCount: number;
  isReadOnlyHistoryMode: boolean;
  isSaving: boolean;
  onReturnHome: () => void;
  onReturnHistory: () => void;
}) {
  const hasNoVisibleQuestions =
    !props.isLoading &&
    props.entry !== null &&
    props.visibleQuestionsCount === 0;

  if (!props.isLoading && !hasNoVisibleQuestions) {
    return null;
  }

  return (
    <SummaryPanel>
      {props.isLoading ? <Text>Loading survey entry session...</Text> : null}
      {hasNoVisibleQuestions ? (
        <React.Fragment>
          <Text>
            No active survey questions are assigned to this survey.
          </Text>
          <ActionRow>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (props.isReadOnlyHistoryMode) {
                  props.onReturnHistory();
                  return;
                }
                props.onReturnHome();
              }}
              isDisabled={props.isSaving}
            >
              {props.isReadOnlyHistoryMode
                ? 'Back to History'
                : 'Return Home'}
            </Button>
          </ActionRow>
        </React.Fragment>
      ) : null}
    </SummaryPanel>
  );
}

function SurveyQuestionHeader(props: { question: RuntimeSurveyQuestion }) {
  const { question } = props;

  return (
    <QuestionMeta>
      <QuestionPrompt>
        <TitleRow>
          <Heading
            level={4}
            size="sm"
            color="$web.semantic.color.text.content.primary"
            textAlign="left"
          >
            {question.questionTitle}
            {question.required ? (
              <React.Fragment>
                {' '}
                <RequiredAsterisk>*</RequiredAsterisk>
              </React.Fragment>
            ) : null}
          </Heading>
          {question.helpLink && question.helpLinkLabel ? (
            <TitleHelpLink
              href={question.helpLink}
              target="_blank"
              rel="noreferrer"
            >
              {question.helpLinkLabel}
            </TitleHelpLink>
          ) : null}
        </TitleRow>
        {question.helpText ? (
          <Text
            color="$web.semantic.color.text.content.secondary"
            style={{ fontSize: '0.9rem' }}
          >
            {question.helpText}
          </Text>
        ) : null}
      </QuestionPrompt>
    </QuestionMeta>
  );
}

function SurveyQuestionAnswerInput(props: {
  question: RuntimeSurveyQuestion;
  answer: SurveyAnswerState;
  index: number;
  isSaving: boolean;
  isReadOnlyHistoryMode: boolean;
  updateAnswer: (questionId: number, nextAnswer: SurveyAnswerState) => void;
  setQuestionTouched: (questionId: number) => void;
}) {
  const { question, answer, index } = props;
  const isDisabled = props.isSaving || props.isReadOnlyHistoryMode;
  const [currencyInputError, setCurrencyInputError] = React.useState<string | null>(null);

  if (isRadioQuestion(question)) {
    return (
      <HiddenLabelChoiceList>
        <ChoiceList>
          <RadioGroup
            label={question.questionTitle}
            isDisabled={isDisabled}
            value={
              answer.selectedOptionIds[0]
                ? String(answer.selectedOptionIds[0])
                : ''
            }
            onChange={(event) => {
              if (props.isReadOnlyHistoryMode) {
                return;
              }

              const selectedOptionId = Number(event.target.value);
              if (!selectedOptionId) {
                return;
              }

              props.updateAnswer(question.questionId, {
                value: getOptionTitle(question, selectedOptionId),
                selectedOptionIds: [selectedOptionId],
              });
              props.setQuestionTouched(question.questionId);
            }}
          >
            <RadioGroup.Column>
              {question.options.map((option) => {
                return (
                  <RadioGroup.Radio
                    key={option.questionOptionId}
                    label={option.optionTitle}
                    value={String(option.questionOptionId)}
                  />
                );
              })}
            </RadioGroup.Column>
          </RadioGroup>
        </ChoiceList>
      </HiddenLabelChoiceList>
    );
  }

  if (isCheckboxQuestion(question)) {
    return (
      <ChoiceList>
        {question.options.map((option) => {
          const isChecked = answer.selectedOptionIds.includes(
            option.questionOptionId
          );
          return (
            <Checkbox
              key={option.questionOptionId}
              inputId={`SURVEY_PAGE.CHECKBOX_${index + 1}_${option.orderId}`}
              label={option.optionTitle}
              isChecked={isChecked}
              isDisabled={isDisabled}
              onChange={() => {
                const nextSelectedOptionIds = isChecked
                  ? answer.selectedOptionIds.filter((selectedOptionId) => {
                      return selectedOptionId !== option.questionOptionId;
                    })
                  : [
                      ...answer.selectedOptionIds,
                      option.questionOptionId,
                    ].sort((left, right) => {
                      return left - right;
                    });

                props.updateAnswer(question.questionId, {
                  value: nextSelectedOptionIds
                    .map((questionOptionId) => {
                      return getOptionTitle(question, questionOptionId);
                    })
                    .join(', '),
                  selectedOptionIds: nextSelectedOptionIds,
                });
                props.setQuestionTouched(question.questionId);
              }}
            />
          );
        })}
      </ChoiceList>
    );
  }

  if (isCurrencyQuestion(question)) {
    return (
      <Field>
        <Label htmlFor={getQuestionInputId(question, index)}>Answer</Label>
        <Input
          id={getQuestionInputId(question, index)}
          type="text"
          placeholder="$0"
          value={answer.value}
          disabled={isDisabled}
          onChange={(event) => {
            props.setQuestionTouched(question.questionId);
            const nextValue = sanitizeCurrencyInput(event.target.value);
            const numericValue = getCurrencyNumericValue(nextValue);
            if (question.maxLength != null && numericValue > question.maxLength) {
              setCurrencyInputError(getCurrencyLimitValidationMessage(question));
              return;
            }

            setCurrencyInputError(null);
            props.updateAnswer(question.questionId, {
              value: nextValue,
              selectedOptionIds: [],
            });
          }}
          onBlur={() => {
            if (currencyInputError) {
              return;
            }

            props.setQuestionTouched(question.questionId);
            props.updateAnswer(question.questionId, {
              value: formatCurrencyDisplay(answer.value),
              selectedOptionIds: [],
            });
          }}
        />
        {currencyInputError ? <Text color="crimson">{currencyInputError}</Text> : null}
      </Field>
    );
  }

  const inputType = isNumericQuestion(question)
    ? 'number'
    : isDateQuestion(question)
      ? 'date'
      : 'text';

  if (isMultilineQuestion(question)) {
    return (
      <Field>
        <Label htmlFor={getQuestionInputId(question, index)}>Answer</Label>
        <Textarea
          id={getQuestionInputId(question, index)}
          value={answer.value}
          maxLength={question.maxLength ?? undefined}
          disabled={isDisabled}
          onChange={(event) => {
            props.setQuestionTouched(question.questionId);
            props.updateAnswer(question.questionId, {
              value: event.target.value,
              selectedOptionIds: [],
            });
          }}
          onBlur={() => {
            props.setQuestionTouched(question.questionId);
          }}
        />
      </Field>
    );
  }

  return (
    <Field>
      <Label htmlFor={getQuestionInputId(question, index)}>Answer</Label>
      <Input
        id={getQuestionInputId(question, index)}
        type={inputType}
        value={answer.value}
        maxLength={
          isSingleLineTextQuestion(question)
            ? question.maxLength ?? undefined
            : undefined
        }
        disabled={isDisabled}
        onChange={(event) => {
          props.setQuestionTouched(question.questionId);
          props.updateAnswer(question.questionId, {
            value: event.target.value,
            selectedOptionIds: [],
          });
        }}
        onBlur={() => {
          props.setQuestionTouched(question.questionId);
        }}
      />
    </Field>
  );
}

function SurveyQuestionsSection(props: {
  isLoading: boolean;
  entry: SurveyEntrySummary | null;
  visibleQuestions: RuntimeSurveyQuestion[];
  answers: SurveyAnswerMap;
  touchedQuestionIds: Record<number, boolean>;
  isSaving: boolean;
  isReadOnlyHistoryMode: boolean;
  requiredAnsweredCount: number;
  requiredVisibleCount: number;
  updateAnswer: (questionId: number, nextAnswer: SurveyAnswerState) => void;
  setQuestionTouched: (questionId: number) => void;
  openDraftPreview: () => void;
  openPreview: () => void;
  onCancel: () => void;
  onReturnToHistory: () => void;
}) {
  if (
    props.isLoading ||
    props.entry === null ||
    props.visibleQuestions.length === 0
  ) {
    return null;
  }

  const entry = props.entry;

  return (
    <React.Fragment>
      <QuestionList>
        {props.visibleQuestions.map((question, index) => {
          const answer = props.answers[question.questionId] ?? {
            value: '',
            selectedOptionIds: [],
          };
          const prefillValue =
            index === 0 && !isOptionQuestion(question) && entry?.selectedCustomer
              ? getPreferredFirstQuestionAnswerValue(entry.selectedCustomer)
              : undefined;
          const validationMessage = props.touchedQuestionIds[
            question.questionId
          ]
            ? getValidationMessage(question, answer, prefillValue)
            : null;
          const hasValidationError = validationMessage !== null;

          return (
            <QuestionCard
              key={question.questionId}
              id={getQuestionAnchorId(question.questionId)}
              style={{
                borderLeftColor: hasValidationError
                  ? '#c2410c'
                  : question.required
                    ? '#2d607f'
                    : '#b8cad8',
                borderLeftWidth: question.required ? '6px' : '4px',
                backgroundColor: hasValidationError ? '#fff7ed' : undefined,
              }}
            >
              <SurveyQuestionHeader question={question} />
              <SurveyQuestionAnswerInput
                question={question}
                answer={answer}
                index={index}
                isSaving={props.isSaving}
                isReadOnlyHistoryMode={props.isReadOnlyHistoryMode}
                updateAnswer={props.updateAnswer}
                setQuestionTouched={props.setQuestionTouched}
              />

              {validationMessage ? (
                <Text color="crimson">
                  {validationMessage}
                </Text>
              ) : null}
            </QuestionCard>
          );
        })}
      </QuestionList>

      {!props.isReadOnlyHistoryMode ? (
        <ActionRow>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={props.onCancel}
            isDisabled={props.isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="filled"
            size="sm"
            onClick={props.openDraftPreview}
            isDisabled={props.isSaving}
          >
            Save as Draft
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={props.openPreview}
            isDisabled={
              props.isSaving ||
              props.requiredAnsweredCount < props.requiredVisibleCount
            }
          >
            Review and Submit
          </Button>

        </ActionRow>
      ) : (
        <ActionRow>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={props.onReturnToHistory}
            isDisabled={props.isSaving}
          >
            Back to History
          </Button>
        </ActionRow>
      )}
    </React.Fragment>
  );
}

function getPreviewQuestions(
  visibleQuestions: RuntimeSurveyQuestion[],
  answers: SurveyAnswerMap,
  touchedQuestionIds: Record<number, boolean>
) {
  return visibleQuestions.filter((question, index) => {
    const isLastQuestion = index === visibleQuestions.length - 1;
    if (!isLastQuestion) return true;

    const answer = answers[question.questionId];
    const isAnswered = Boolean(answer) && hasAnswer(question, answer!);
    // Hide the trailing question from the preview only while it's still untouched and blank.
    return isAnswered || touchedQuestionIds[question.questionId];
  });
}

function SurveyPreviewDialog(props: {
  showPreview: boolean;
  isDraftPreview: boolean;
  isSaving: boolean;
  attested: boolean;
  error: string | null;
  visibleQuestions: RuntimeSurveyQuestion[];
  answers: SurveyAnswerMap;
  touchedQuestionIds: Record<number, boolean>;
  onClose: () => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  onAttestedChange: (checked: boolean) => void;
}) {
  const previewQuestions = getPreviewQuestions(
    props.visibleQuestions,
    props.answers,
    props.touchedQuestionIds
  );

  return (
    <SharedDialog
      isOpen={props.showPreview}
      onClose={props.onClose}
      title="Review your answers"
      description={
        props.isDraftPreview
          ? 'Confirm the answers below, then save your progress as a draft.'
          : 'Confirm the answers below, complete attestation, and submit the survey.'
      }
      size="lg"
      primaryAction={
        props.isDraftPreview
          ? {
              label: 'Save Draft',
              onClick: props.onSaveDraft,
              disabled: props.isSaving,
              loading: props.isSaving,
            }
          : {
              label: 'Submit RFI',
              onClick: props.onSubmit,
              disabled: props.isSaving || !props.attested,
              loading: props.isSaving,
            }
      }
      secondaryAction={{
        label: 'Cancel',
        onClick: props.onClose,
        variant: 'outline',
        disabled: props.isSaving,
      }}
    >
      <PreviewList>
        <V1Table
          title="Survey answers"
          highlightRowOnHover
          columns={[
            { key: 'question', name: 'Question' },
            { key: 'answer', name: 'Your Answer' },
          ]}
          rows={previewQuestions.map((question) => {
            return {
              id: question.questionId,
              question: question.questionTitle,
              answer: getAnswerText(question, props.answers[question.questionId]),
            };
          })}
        />
        {!props.isDraftPreview ? (
          <React.Fragment>
            <Divider margin="0" />
                        <AttestationBox>
              <Checkbox
                inputId="SURVEY_PAGE.ATTESTATION_1"
                label="I attest that the above information is accurate to the best of my knowledge. Also, if I am a delegate of the Employer Group, I was given permission and authority to fill this information out on behalf of the Employer Group."
                isChecked={props.attested}
                isDisabled={props.isSaving}
                onChange={(event) => {
                  props.onAttestedChange(event.target.checked);
                }}
              />
            </AttestationBox>
            {props.error ? (
              <Alert status="error" title="Attestation required" dismissible={false}>
                {props.error}
              </Alert>
            ) : null}
          </React.Fragment>
        ) : null}
      </PreviewList>
    </SharedDialog>
  );
}

function SurveySaveSuccessDialog(props: {
  showSaveSuccessDialog: boolean;
  saveSuccessWasFinalSubmit: boolean;
  saveSuccessResponse: SurveyResponseRecord | null;
  isSaving: boolean;
  onAcknowledge: () => void;
}) {
  return (
    <SharedDialog
      isOpen={props.showSaveSuccessDialog}
      onClose={() => {
        return;
      }}
      title={
        props.saveSuccessWasFinalSubmit
          ? 'The RFI has been submitted successfully'
          : 'A DRAFT of the RFI has been saved successfully'
      }
      description={buildSaveSuccessDescription(props.saveSuccessWasFinalSubmit)}
      dismissible={false}
      primaryAction={{
        label: 'OK',
        onClick: props.onAcknowledge,
        disabled: props.isSaving,
      }}
    >
      {props.saveSuccessWasFinalSubmit ? (
        <V1Table
          title="Submission summary"
          columns={[
            { key: 'label', name: 'Detail' },
            { key: 'value', name: 'Value' },
          ]}
          rows={[
            {
              id: 'started',
              label: 'Date started',
              value: formatDisplayDateTime(
                props.saveSuccessResponse?.startDate,
                'Not available'
              ),
            },
            {
              id: 'completed',
              label: 'Date completed',
              value: formatDisplayDateTime(
                props.saveSuccessResponse?.completedDate,
                'Not available'
              ),
            },
          ]}
        />
      ) : null}
    </SharedDialog>
  );
}

export const Survey = () => {
  const { session } = useAuth();
  const { setError: setGlobalError } = useGlobalError();
  const entryId = React.useMemo(() => {
    return getEntryIdFromPath();
  }, []);
  const isReadOnlyHistoryMode = React.useMemo(() => {
    return isHistoryReadOnlyMode();
  }, []);
  const [entry, setEntry] = React.useState<SurveyEntrySummary | null>(null);
  const [questions, setQuestions] = React.useState<RuntimeSurveyQuestion[]>([]);
  const [savedResponse, setSavedResponse] =
    React.useState<SurveyResponseRecord | null>(null);
  const [answers, setAnswers] = React.useState<SurveyAnswerMap>({});
  const [touchedQuestionIds, setTouchedQuestionIds] = React.useState<
    Record<number, boolean>
  >({});
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const [isDraftPreview, setIsDraftPreview] = React.useState(false);
  const [showSaveSuccessDialog, setShowSaveSuccessDialog] =
    React.useState(false);
  const [attested, setAttested] = React.useState(false);
  const [saveSuccessResponse, setSaveSuccessResponse] =
    React.useState<SurveyResponseRecord | null>(null);
  const [saveSuccessWasFinalSubmit, setSaveSuccessWasFinalSubmit] =
    React.useState(false);
  const [validationSummary, setValidationSummary] = React.useState<
    Array<{ questionId: number; questionTitle: string; message: string }>
  >([]);

  React.useEffect(() => {
    const previousBodyMargin = document.body.style.margin;

    document.body.style.margin = '0';

    return () => {
      document.body.style.margin = previousBodyMargin;
    };
  }, []);

  const visibleQuestionIds = React.useMemo(() => {
    return computeVisibleQuestionIds(questions, answers);
  }, [answers, questions]);

  const visibleQuestions = React.useMemo(() => {
    return questions.filter((question) => {
      return visibleQuestionIds.includes(question.questionId);
    });
  }, [questions, visibleQuestionIds]);

  const visibleQuestionIdSet = React.useMemo(() => {
    return new Set(visibleQuestionIds);
  }, [visibleQuestionIds]);

  React.useEffect(() => {
    setTouchedQuestionIds((previous) => {
      const nextTouched = Object.fromEntries(
        Object.entries(previous).filter(([questionId]) => {
          return visibleQuestionIdSet.has(Number(questionId));
        })
      ) as Record<number, boolean>;

      return Object.keys(nextTouched).length === Object.keys(previous).length
        ? previous
        : nextTouched;
    });
    setValidationSummary((previous) => {
      const nextSummary = previous.filter((issue) => {
        return visibleQuestionIdSet.has(issue.questionId);
      });

      return nextSummary.length === previous.length ? previous : nextSummary;
    });
  }, [visibleQuestionIdSet]);

  const requiredVisibleCount = React.useMemo(() => {
    return visibleQuestions.filter((question) => {
      return question.required;
    }).length;
  }, [visibleQuestions]);

  const requiredAnsweredCount = React.useMemo(() => {
    return visibleQuestions.filter((question) => {
      return (
        question.required && hasAnswer(question, answers[question.questionId])
      );
    }).length;
  }, [answers, visibleQuestions]);

  const progressState = React.useMemo(() => {
    return computeProgressState(questions, answers);
  }, [answers, questions]);

  const showBackendError = React.useCallback(
    (title: string, fallbackMessage: string, nextError: unknown) => {
      const message =
        nextError instanceof Error ? nextError.message : fallbackMessage;
      setGlobalError({
        title,
        message,
      });
    },
    [setGlobalError]
  );

  React.useEffect(() => {
    let isMounted = true;

    const loadEntry = async () => {
      if (!session?.token) {
        if (isMounted) {
          setGlobalError({
            title: 'Session expired',
            message: 'No authenticated session found. Please sign in again.',
            isAuthError: true,
          });
          setIsLoading(false);
        }
        return;
      }

      if (!entryId) {
        if (isMounted) {
          setGlobalError({
            title: 'Invalid URL',
            message: 'Invalid survey entry URL.',
          });
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const nextEntry = await getSurveyEntry({
          entryId,
        });

        const [nextQuestions, nextSavedResponse] = await Promise.all([
          getSurveyQuestionsBySurveyId({
            surveyId: nextEntry.activeSurvey.surveyId,
          }),
          getSurveyResponseByCustomerIdentity({
            customerNumber: nextEntry.selectedCustomer.customerNumber,
            fundingType: nextEntry.selectedCustomer.fundingType,
            reportingYear: Number(nextEntry.selectedCustomer.surveyYear),
            surveyId: nextEntry.activeSurvey.surveyId,
          }),
        ]);

        if (isMounted) {
          const hydratedAnswers = buildHydratedAnswers(
            nextQuestions,
            nextSavedResponse,
            {
              employerGroupName: nextEntry.selectedCustomer.employerGroupName,
              customerNumber: nextEntry.selectedCustomer.customerNumber,
            }
          );
          const initialVisibleIds = computeVisibleQuestionIds(
            nextQuestions,
            hydratedAnswers
          );
          setEntry(nextEntry);
          setQuestions(nextQuestions);
          setSavedResponse(nextSavedResponse);
          setAnswers(pruneAnswersToVisible(hydratedAnswers, initialVisibleIds));
          setTouchedQuestionIds({});
          setAttested(false);
          setValidationSummary([]);
        }
      } catch (nextError) {
        if (isMounted) {
          setEntry(null);
          setQuestions([]);
          setSavedResponse(null);
          setAnswers({});
          setError(null);
          showBackendError(
            'Unable to load survey entry session',
            'Unable to load survey entry session.',
            nextError
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadEntry();

    return () => {
      isMounted = false;
    };
  }, [entryId, session?.token, showBackendError]);

  const returnToHome = React.useCallback(
    (returnAction: 'cancel' | 'save') => {
      if (!entry) {
        return;
      }

      writeInternalSearchContext({ entry, returnAction });

      setActionMessage(
        returnAction === 'save'
          ? 'Survey responses saved. Returning to Home...'
          : 'Survey cancelled. Returning to Home...'
      );

      window.setTimeout(() => {
        navigateTo('/home');
      }, 120);
    },
    [entry]
  );

  const returnToHistory = React.useCallback(() => {
    navigateTo('/historicdata');
  }, []);

  const setQuestionTouched = React.useCallback((questionId: number) => {
    setTouchedQuestionIds((prev) => {
      return {
        ...prev,
        [questionId]: true,
      };
    });
  }, []);

  const focusQuestionSection = React.useCallback((questionId: number) => {
    const questionCard = window.document.getElementById(
      getQuestionAnchorId(questionId)
    );
    if (!questionCard) {
      return;
    }

    questionCard.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const focusTarget = questionCard.querySelector<HTMLElement>(
      'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusTarget) {
      window.setTimeout(() => {
        focusTarget.focus();
      }, 120);
    }
  }, []);

  const updateAnswer = React.useCallback(
    (questionId: number, nextAnswer: SurveyAnswerState) => {
      setAnswers((prev) => {
        const mergedAnswers = {
          ...prev,
          [questionId]: nextAnswer,
        };
        const nextVisibleQuestionIds = computeVisibleQuestionIds(
          questions,
          mergedAnswers
        );
        return pruneAnswersToVisible(mergedAnswers, nextVisibleQuestionIds);
      });
    },
    [questions]
  );

  const validateVisibleQuestions = React.useCallback((isDraft = false) => {
    const resolved = resolveAnswersForValidation({
      questions,
      answers,
    });

    if (resolved.answers !== answers) {
      setAnswers(resolved.answers);
    }

    const firstQuestionId = questions[0]?.questionId;
    const firstQuestionPrefillValue = entry?.selectedCustomer
      ? getPreferredFirstQuestionAnswerValue(entry.selectedCustomer)
      : undefined;

    const finalVisibleQuestionId = resolved.visibleQuestions.at(-1)?.questionId;
    const issues = resolved.visibleQuestions
      .filter((question) => {
        return touchedQuestionIds[question.questionId];
      })
      .map((question) => {
        const answer = resolved.answers[question.questionId];
        const prefillValue =
          question.questionId === firstQuestionId && !isOptionQuestion(question)
            ? firstQuestionPrefillValue
            : undefined;
        const message = getValidationMessage(question, answer, prefillValue);
        if (
          isDraft &&
          question.questionId === finalVisibleQuestionId &&
          question.questionId !== firstQuestionId &&
          question.required &&
          !hasAnswer(question, answer)
        ) {
          return null;
        }
        return message
          ? {
              questionId: question.questionId,
              questionTitle: question.questionTitle,
              message,
            }
          : null;
      })
      .filter(
        (
          issue
        ): issue is {
          questionId: number;
          questionTitle: string;
          message: string;
        } => {
          return issue !== null;
        }
      );

    setValidationSummary(issues);
    if (issues.length > 0) {
      focusQuestionSection(issues[0]!.questionId);
    }
    return issues;
  }, [answers, entry, focusQuestionSection, questions, touchedQuestionIds]);

  const handleSave = React.useCallback(
    async (isFinalSubmit: boolean) => {
      const sessionError = getSaveSessionError(entry, session);
      if (sessionError) {
        setGlobalError(sessionError);
        return;
      }

      const persistedUserId = resolvePersistedUserId(session, savedResponse);
      if (!persistedUserId) {
        setGlobalError({
          title: 'Session error',
          message:
            'Authenticated user context is incomplete. Please sign in again.',
          isAuthError: true,
        });
        return;
      }

      const resolved = resolveAnswersForValidation({
        questions,
        answers,
      });

      if (resolved.answers !== answers) {
        setAnswers(resolved.answers);
      }

      const issues = validateVisibleQuestions(!isFinalSubmit);
      if (!isFinalSubmit && issues.length > 0) {
        return;
      }

      if (isFinalSubmit) {
        if (shouldBlockFinalSubmit(issues.length, attested)) {
          if (!attested) {
            setError(SURVEY_VALIDATION.attestationRequiredMessage);
          }
          return;
        }
      }

      setIsSaving(true);
      setError(null);

      try {
        const payload = buildSavePayload({
          entry: entry!,
          sessionDisplayName: session!.displayName,
          persistedUserId,
          questions,
          answers: resolved.answers,
          visibleQuestionIds: resolved.visibleQuestionIds,
          savedResponse,
          isFinalSubmit,
        });

        const persistedResponse = await saveSurveyResponse({
          payload,
        });

        setSavedResponse(persistedResponse);
        setShowPreview(false);
        setIsDraftPreview(false);
        setAttested(false);
        setSaveSuccessResponse(persistedResponse);
        setSaveSuccessWasFinalSubmit(isFinalSubmit);
        setShowSaveSuccessDialog(true);
      } catch (nextError) {
        setError(null);
        showBackendError(
          'Unable to save survey response',
          'Unable to save survey response.',
          nextError
        );
      } finally {
        setIsSaving(false);
      }
    },
    [
      answers,
      attested,
      entry,
      questions,
      returnToHome,
      savedResponse,
      session,
      showBackendError,
      validateVisibleQuestions,
      visibleQuestionIds,
    ]
  );

  const openPreview = React.useCallback(() => {
    const issues = validateVisibleQuestions();
    if (issues.length > 0) {
      setShowPreview(false);
      return;
    }

    setError(null);
    setIsDraftPreview(false);
    setShowPreview(true);
  }, [validateVisibleQuestions]);

  const openDraftPreview = React.useCallback(() => {
    const resolved = resolveAnswersForValidation({ questions, answers });
    if (resolved.answers !== answers) {
      setAnswers(resolved.answers);
    }

    const issues = validateVisibleQuestions(true);
    if (issues.length > 0) {
      return;
    }

    setError(null);
    setIsDraftPreview(true);
    setShowPreview(true);
  }, [answers, questions, validateVisibleQuestions]);

  const policyValues = entry
    ? parsePolicyValues(
        entry.selectedCustomer.policyList,
        entry.selectedCustomer.policyNumber
      )
    : [];
  const hasPolicyOverflowRow = policyValues.length > 6;
  const policyDisplayValue =
    policyValues.length > 0 ? policyValues.join(', ') : '—';
  const detailColumnCount = hasPolicyOverflowRow ? 5 : 6;

  return (
    <React.Fragment>
      <Router.MetaTags title="Survey" />
      <SurveyLayout>
        <SurveyProgressRail
          isLoading={isLoading}
          entry={entry}
          questions={questions}
          visibleQuestionIdSet={visibleQuestionIdSet}
          progressState={progressState}
          onFocusQuestion={focusQuestionSection}
        />
        <Page>
          <SurveyContextPanel
            entry={entry}
            isInternalUser={session?.authType === 'INTERNAL'}
            hasPolicyOverflowRow={hasPolicyOverflowRow}
            policyDisplayValue={policyDisplayValue}
            detailColumnCount={detailColumnCount}
          />

          <Heading
            level={3}
            size="md"
            color="$web.semantic.color.text.content.primary"
            textAlign="left"
          >
            {getSurveyHeadingText(entry)}
          </Heading>

          <SurveyValidationSummary validationSummary={validationSummary} />

          <SurveyLoadingOrEmptyPanel
            isLoading={isLoading}
            entry={entry}
            visibleQuestionsCount={visibleQuestions.length}
            isReadOnlyHistoryMode={isReadOnlyHistoryMode}
            isSaving={isSaving}
            onReturnHome={() => {
              returnToHome('cancel');
            }}
            onReturnHistory={returnToHistory}
          />

          <SurveyQuestionsSection
            isLoading={isLoading}
            entry={entry}
            visibleQuestions={visibleQuestions}
            answers={answers}
            touchedQuestionIds={touchedQuestionIds}
            isSaving={isSaving}
            isReadOnlyHistoryMode={isReadOnlyHistoryMode}
            requiredAnsweredCount={requiredAnsweredCount}
            requiredVisibleCount={requiredVisibleCount}
            updateAnswer={updateAnswer}
            setQuestionTouched={setQuestionTouched}
            openDraftPreview={openDraftPreview}
            openPreview={openPreview}
            onCancel={() => {
              returnToHome('cancel');
            }}
            onReturnToHistory={returnToHistory}
          />

          {actionMessage ? <Text>{actionMessage}</Text> : null}

          {isSaving && (
            <PageSpinner label="Submitting survey response" blocking />
          )}

          <SurveyPreviewDialog
            showPreview={showPreview}
            isDraftPreview={isDraftPreview}
            isSaving={isSaving}
            attested={attested}
            error={error}
            visibleQuestions={visibleQuestions}
            answers={answers}
            touchedQuestionIds={touchedQuestionIds}
            onClose={() => {
              setShowPreview(false);
              setIsDraftPreview(false);
              setAttested(false);
            }}
            onSaveDraft={() => {
              void handleSave(false);
            }}
            onSubmit={() => {
              void handleSave(true);
            }}
            onAttestedChange={(checked) => {
              setAttested(checked);
              if (checked) {
                setError(null);
              }
            }}
          />

          <SurveySaveSuccessDialog
            showSaveSuccessDialog={showSaveSuccessDialog}
            saveSuccessWasFinalSubmit={saveSuccessWasFinalSubmit}
            saveSuccessResponse={saveSuccessResponse}
            isSaving={isSaving}
            onAcknowledge={() => {
              setShowSaveSuccessDialog(false);
              setSaveSuccessResponse(null);
              returnToHome('save');
            }}
          />
        </Page>
      </SurveyLayout>
    </React.Fragment>
  );
};
